import { useEffect, useState } from 'react';
import { IconSunHigh, IconMoon, IconX, IconCheck, IconAlertTriangle, IconClockHour6 } from '@tabler/icons-react';
import { Modal } from './ui';
import { useDaySession, OpenDayBlockedError, type UnreviewedAutoClose } from '../hooks/useDaySession';
import { useManagerApproval } from '../hooks/useManagerApproval';

/**
 * Open-day + close-day modal pair (Phase 1.6b).
 *
 * Owner-specified flow:
 *   AM: supervisor opens the day with the opening cash float
 *   PM: supervisor closes the day with counted cash + closing float
 *       (variance over the configurable threshold requires
 *        manager-PIN step-up; useManagerApproval is wired in)
 *
 * Cohesion rule 1: ONE place that opens/closes the day. PosPage
 * imports OpenDayButton + CloseDayButton; nowhere else has its own
 * UI for this.
 */

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// OpenDayModal
// ---------------------------------------------------------------------------

export function OpenDayModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { openDay } = useDaySession();
  const [floatRand, setFloatRand] = useState('500');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Phase 4.15 — when the server says a previous auto-closed session
  // must be reviewed first, switch this modal into review mode.
  const [reviewBlocker, setReviewBlocker] = useState<UnreviewedAutoClose | null>(null);

  const onSubmit = async () => {
    setError(null);
    const cents = Math.round(parseFloat(floatRand || '0') * 100);
    if (!Number.isFinite(cents) || cents < 0) {
      setError('Opening float must be a positive amount');
      return;
    }
    setSubmitting(true);
    try {
      await openDay(cents);
      onClose();
    } catch (err) {
      if (err instanceof OpenDayBlockedError && err.autoClosed) {
        setReviewBlocker(err.autoClosed);
        setError(null);
      } else {
        setError((err as Error).message || 'Could not open day');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (reviewBlocker) {
    return (
      <AutoCloseReviewModal
        open={open}
        autoClosed={reviewBlocker}
        onClose={() => { setReviewBlocker(null); onClose(); }}
        onDone={async () => {
          // After review, immediately try to open the day.
          setReviewBlocker(null);
          setSubmitting(true);
          try {
            const cents = Math.round(parseFloat(floatRand || '0') * 100);
            await openDay(cents);
            onClose();
          } catch (err) {
            setError((err as Error).message || 'Could not open day');
          } finally {
            setSubmitting(false);
          }
        }}
      />
    );
  }

  return (
    <Modal open={open} onClose={onClose} hideClose size="sm">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-lg bg-success-soft text-success inline-flex items-center justify-center flex-shrink-0">
          <IconSunHigh size={22} stroke={2} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-12 font-bold uppercase tracking-widest text-success mb-1">Open day</p>
          <h3 className="text-18 font-bold text-heading">Start trading session</h3>
          <p className="text-13 font-medium text-gray-700 mt-1.5 leading-snug">
            Declare the cash you're starting with in the drawer. Cashiers can ring orders once the day is open.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-light hover:text-heading transition-colors"
        >
          <IconX size={18} stroke={2.25} />
        </button>
      </div>

      <label className="block text-14 font-bold text-heading mb-2">
        Opening cash float (R)
      </label>
      <div className="relative mb-2">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R</span>
        <input
          type="number"
          min="0"
          step="any"
          value={floatRand}
          onChange={(e) => setFloatRand(e.target.value)}
          className="w-full h-14 pl-8 pr-3 rounded-md border-2 border-border focus:border-success text-22 font-bold text-heading tabular-nums outline-none transition-colors"
        />
      </div>

      {error && (
        <p className="text-13 text-danger font-bold mb-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-md bg-success text-[#FFFFFF] text-14 font-bold hover:bg-success-hover shadow-sm disabled:opacity-60 transition-colors"
        >
          <IconCheck size={18} stroke={2.5} />
          {submitting ? 'Opening…' : 'Open day'}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// CloseDayModal
// ---------------------------------------------------------------------------

export function CloseDayModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { current, closeDay } = useDaySession();
  const askApproval = useManagerApproval();
  const [countedRand, setCountedRand] = useState('');
  const [closingFloatRand, setClosingFloatRand] = useState('500');
  const [closeNotes, setCloseNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Phase 2.9 — fetch live X-report so "Expected in drawer" is the
  // real number (opening float + cash sales + paid-ins - paid-outs)
  // instead of the v1 stub.
  const [expectedFromReport, setExpectedFromReport] = useState<number | null>(null);
  const [netSalesCents, setNetSalesCents] = useState<number | null>(null);
  const [cashSalesNetCents, setCashSalesNetCents] = useState<number | null>(null);
  const [paidInCents, setPaidInCents] = useState<number | null>(null);
  const [paidOutCents, setPaidOutCents] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/day-sessions/x-report', { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setExpectedFromReport(data.report?.cash?.expectedCashCents ?? null);
        setNetSalesCents(data.report?.totals?.netSalesCents ?? null);
        setCashSalesNetCents(data.report?.cash?.cashSalesNetCents ?? null);
        setPaidInCents(data.report?.cash?.paidInCents ?? null);
        setPaidOutCents(data.report?.cash?.paidOutCents ?? null);
      } catch { /* keep stub */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const expectedCents = expectedFromReport ?? (current?.openingFloatCents ?? 0);
  const countedCents = Math.round(parseFloat(countedRand || '0') * 100);
  const closingFloatCents = Math.round(parseFloat(closingFloatRand || '0') * 100);
  const varianceCents = countedCents - expectedCents;

  const doClose = async () => {
    setSubmitting(true);
    try {
      await closeDay({
        countedCashCents: countedCents,
        closingFloatCents,
        closeNotes: closeNotes.trim() || undefined,
      });
      onClose();
    } catch (err) {
      const e = err as Error & { thresholdCents?: number };
      // If the server rejects for variance-over-threshold, pop the
      // manager-PIN modal and retry once the elevation cookie is set.
      if (e.message.startsWith('Variance over threshold')) {
        const approved = await askApproval({
          action: 'Close day with variance',
          description: `Variance of ${fmtR(Math.abs(varianceCents))} exceeds the configured threshold. Manager PIN required to close.`,
        });
        if (!approved) {
          setError('Manager approval cancelled. Day still open.');
          setSubmitting(false);
          return;
        }
        // Retry — elevation cookie now set
        try {
          await closeDay({
            countedCashCents: countedCents,
            closingFloatCents,
            closeNotes: closeNotes.trim() || undefined,
          });
          onClose();
          return;
        } catch (retryErr) {
          setError((retryErr as Error).message || 'Could not close day');
        }
      } else {
        setError(e.message || 'Could not close day');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async () => {
    setError(null);
    if (!Number.isFinite(countedCents) || countedCents < 0) {
      setError('Counted cash must be a positive amount');
      return;
    }
    if (!Number.isFinite(closingFloatCents) || closingFloatCents < 0) {
      setError('Closing float must be a positive amount');
      return;
    }
    await doClose();
  };

  return (
    <Modal open={open} onClose={onClose} hideClose size="lg">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-lg bg-warning-soft text-warning inline-flex items-center justify-center flex-shrink-0">
          <IconMoon size={22} stroke={2} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-12 font-bold uppercase tracking-widest text-warning mb-1">Close day</p>
          <h3 className="text-18 font-bold text-heading">End trading session</h3>
          <p className="text-13 font-medium text-gray-700 mt-1.5 leading-snug">
            Count the cash in the drawer and declare it below. Variance is calculated automatically; large variances require manager approval.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-light hover:text-heading transition-colors"
        >
          <IconX size={18} stroke={2.25} />
        </button>
      </div>

      {/* Expected vs counted comparison */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-3 rounded-md bg-light border border-border">
          <p className="text-11 font-bold uppercase tracking-wider text-gray-700 mb-1">Expected in drawer</p>
          <p className="text-18 font-bold text-heading tabular-nums">{fmtR(expectedCents)}</p>
          <p className="text-11 text-gray-700 mt-0.5">
            {expectedFromReport === null
              ? 'Loading live numbers…'
              : `Float ${fmtR(current?.openingFloatCents ?? 0)} + cash sales ${fmtR(cashSalesNetCents ?? 0)} + in ${fmtR(paidInCents ?? 0)} − out ${fmtR(paidOutCents ?? 0)}`}
          </p>
        </div>
        <div className={[
          'p-3 rounded-md border',
          Math.abs(varianceCents) > 0 && countedCents > 0
            ? Math.abs(varianceCents) > 20000
              ? 'bg-danger-soft border-danger/40'
              : 'bg-warning-soft border-warning/40'
            : 'bg-light border-border',
        ].join(' ')}>
          <p className="text-11 font-bold uppercase tracking-wider text-gray-700 mb-1">Variance</p>
          <p className={[
            'text-18 font-bold tabular-nums',
            varianceCents === 0 ? 'text-heading'
              : varianceCents > 0 ? 'text-success' : 'text-danger',
          ].join(' ')}>
            {varianceCents > 0 ? '+' : ''}{fmtR(varianceCents)}
          </p>
          <p className="text-11 text-gray-700 mt-0.5">counted − expected</p>
        </div>
      </div>

      {netSalesCents !== null && (
        <div className="mb-5 px-3 py-2 rounded-md bg-primary-soft border border-primary/20 text-13 font-semibold text-primary flex items-center justify-between">
          <span className="uppercase tracking-wider">Today's net sales</span>
          <span className="font-bold tabular-nums">{fmtR(netSalesCents)}</span>
        </div>
      )}

      <div className="mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-14 font-bold text-heading mb-2">
              Counted cash in drawer (R)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R</span>
              <input
                type="number"
                min="0"
                step="any"
                value={countedRand}
                onChange={(e) => setCountedRand(e.target.value)}
                placeholder="0.00"
                className="w-full h-14 pl-8 pr-3 rounded-md border-2 border-border focus:border-warning text-22 font-bold text-heading tabular-nums outline-none transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-14 font-bold text-heading mb-2">
              Closing float (to leave in drawer)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R</span>
              <input
                type="number"
                min="0"
                step="any"
                value={closingFloatRand}
                onChange={(e) => setClosingFloatRand(e.target.value)}
                className="w-full h-14 pl-8 pr-3 rounded-md border-2 border-border focus:border-warning text-22 font-bold text-heading tabular-nums outline-none transition-colors"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-14 font-bold text-heading mb-2">
            Notes (optional)
          </label>
          <textarea
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder="Optional: anything unusual about today?"
            rows={2}
            className="w-full px-3 py-2 rounded-md border-2 border-border focus:border-warning text-14 font-medium text-heading outline-none transition-colors resize-none"
          />
        </div>
      </div>

      {error && (
        <p className="text-13 text-danger font-bold mb-3" role="alert">
          <IconAlertTriangle size={14} stroke={2.5} className="inline-block mr-1 -mt-0.5" />
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 pt-5 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-md bg-warning text-[#FFFFFF] text-14 font-bold hover:bg-warning-hover shadow-sm disabled:opacity-60 transition-colors"
        >
          <IconCheck size={18} stroke={2.5} />
          {submitting ? 'Closing…' : 'Close day'}
        </button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Phase 4.15 — Auto-close review modal                                       */
/* -------------------------------------------------------------------------- */

function AutoCloseReviewModal({
  open, autoClosed, onClose, onDone,
}: {
  open: boolean;
  autoClosed: UnreviewedAutoClose;
  onClose: () => void;
  onDone: () => void;
}) {
  const { reviewAutoClosed } = useDaySession();
  const [countedRand, setCountedRand] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const expected = autoClosed.expectedCashCents ?? 0;
  const counted  = Math.round(parseFloat(countedRand || '0') * 100);
  const variance = counted - expected;

  // Allow "0" or empty drawer (legitimate case — empty till after auto-close).
  // We require the field to have been touched (non-empty string) so the
  // cashier consciously enters a value rather than skipping it.
  const countedTouched = countedRand.trim().length > 0;
  const reasonOk = notes.trim().length >= 3;
  const canSubmit = !submitting && countedTouched && reasonOk;

  const submit = async () => {
    setErr(null);
    if (!countedTouched) {
      setErr('Enter what you counted in the drawer (R0.00 is fine if empty).');
      return;
    }
    if (!reasonOk) {
      setErr('Please add a short reason so we know why the day was left open.');
      return;
    }
    setSubmitting(true);
    try {
      await reviewAutoClosed(autoClosed.id, {
        countedCashCents: counted,
        reviewNotes: notes.trim(),
      });
      onDone();
    } catch (e) {
      setErr((e as Error).message || 'Could not save the review');
    } finally {
      setSubmitting(false);
    }
  };

  const closedAtLabel = autoClosed.autoClosedAt
    ? new Date(autoClosed.autoClosedAt).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })
    : 'earlier';

  return (
    <Modal open={open} onClose={onClose} hideClose size="md">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-lg bg-warning-soft text-warning inline-flex items-center justify-center flex-shrink-0">
          <IconClockHour6 size={22} stroke={2} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-12 font-bold uppercase tracking-widest text-warning mb-1">Review required</p>
          <h3 className="text-18 font-bold text-heading">A previous session was auto-closed</h3>
          <p className="text-13 font-medium text-gray-700 mt-1.5 leading-snug">
            The system closed an open day session because it was left running past its scheduled time.
            Please file what you counted in the drawer this morning + a brief reason before opening today.
          </p>
        </div>
      </div>

      <div className="rounded-md bg-light border border-border-soft p-3 mb-4 text-13">
        <div className="flex items-center justify-between mb-1">
          <span className="text-12 font-bold uppercase tracking-wider text-gray-700">Closed at</span>
          <span className="font-bold text-heading tabular-nums">{closedAtLabel}</span>
        </div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-12 font-bold uppercase tracking-wider text-gray-700">Opened by</span>
          <span className="font-bold text-heading">{autoClosed.openedBy?.name ?? '—'}</span>
        </div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-12 font-bold uppercase tracking-wider text-gray-700">Opening float</span>
          <span className="font-bold text-heading tabular-nums">{fmtR(autoClosed.openingFloatCents)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-12 font-bold uppercase tracking-wider text-gray-700">Expected cash</span>
          <span className="font-bold text-heading tabular-nums">{fmtR(expected)}</span>
        </div>
        {autoClosed.autoCloseReason && (
          <p className="text-12 italic text-gray-600 mt-2 pt-2 border-t border-border-soft">
            System note: {autoClosed.autoCloseReason}
          </p>
        )}
      </div>

      <label className="block text-14 font-bold text-heading mb-2">Counted cash in the drawer (R)</label>
      <div className="relative mb-3">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R</span>
        <input
          type="number"
          min="0"
          step="any"
          value={countedRand}
          onChange={(e) => setCountedRand(e.target.value)}
          placeholder="0.00"
          className="w-full h-14 pl-8 pr-3 rounded-md border-2 border-border focus:border-warning text-22 font-bold text-heading tabular-nums outline-none transition-colors"
        />
      </div>
      {countedRand && (
        <p className={`text-13 font-bold mb-3 ${variance === 0 ? 'text-success' : variance < 0 ? 'text-danger' : 'text-warning'}`}>
          Variance: {variance > 0 ? '+' : ''}{fmtR(variance)}
        </p>
      )}

      <label className="block text-14 font-bold text-heading mb-2">Reason / notes</label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. Forgot to close before leaving, no further sales."
        rows={3}
        className="w-full px-3 py-2 rounded-md border-2 border-border focus:border-warning text-14 font-medium text-heading placeholder-gray-500 outline-none transition-colors resize-none"
      />
      {!reasonOk && notes.length > 0 && (
        <p className="text-12 text-gray-700 mt-1.5">A short reason is enough — even a few words.</p>
      )}

      {err && <p className="text-13 text-danger font-bold mt-2" role="alert">{err}</p>}

      <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-warning text-[#FFFFFF] text-14 font-bold hover:bg-warning-hover transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <IconCheck size={16} stroke={2.5} />
          {submitting ? 'Saving…' : 'File review and open today'}
        </button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Phase 4.15 — Reopen for late sale modal                                    */
/* -------------------------------------------------------------------------- */

export function ReopenDayModal({
  open, sessionId, onClose, onReopened,
}: {
  open: boolean;
  sessionId: string;
  onClose: () => void;
  onReopened: () => void;
}) {
  const { reopenDay } = useDaySession();
  const ask = useManagerApproval();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setErr(null);
    if (reason.trim().length < 10) {
      setErr('A reason is required (10+ chars).');
      return;
    }
    setSubmitting(true);
    try {
      // Manager-PIN gate (the server also enforces requireElevation).
      const approved = await ask({
        action: 'Reopen the day for a late sale',
        description: reason.trim(),
      });
      if (!approved) {
        setSubmitting(false);
        return;
      }
      await reopenDay(sessionId, reason.trim());
      onReopened();
    } catch (e) {
      setErr((e as Error).message || 'Could not reopen the day');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} hideClose size="md">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-lg bg-info-soft text-info inline-flex items-center justify-center flex-shrink-0">
          <IconSunHigh size={22} stroke={2} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-12 font-bold uppercase tracking-widest text-info mb-1">Reopen for late sale</p>
          <h3 className="text-18 font-bold text-heading">Unlock the till temporarily</h3>
          <p className="text-13 font-medium text-gray-700 mt-1.5 leading-snug">
            Use this for a walk-in customer after close. The day will auto-close again 60 minutes from now
            if you forget. Manager PIN required.
          </p>
        </div>
      </div>

      <label className="block text-14 font-bold text-heading mb-2">Reason</label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Walk-in customer wants two coffees for the road."
        rows={3}
        className="w-full px-3 py-2 rounded-md border-2 border-border focus:border-info text-14 font-medium text-heading placeholder-gray-500 outline-none transition-colors resize-none"
      />

      {err && <p className="text-13 text-danger font-bold mt-2" role="alert">{err}</p>}

      <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || reason.trim().length < 10}
          className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-info text-[#FFFFFF] text-14 font-bold hover:bg-info-hover transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <IconAlertTriangle size={16} stroke={2.5} />
          {submitting ? 'Working…' : 'Reopen with manager PIN'}
        </button>
      </div>
    </Modal>
  );
}
